import {
  Component,
  Host,
  h,
  Element,
  getAssetPath,
  Method,
  Prop
} from "@stencil/core";
import { Locale } from "../../../common/locale";
import { MercuryBundles } from "@genexus/mercury";
import { ComboBoxItemModel } from "@genexus/chameleon-controls-library/dist/types/components/combo-box/types";
import { mapTypesToComboBoxItemModel } from "./helpers";

const CSS_BUNDLES: MercuryBundles = [
  "resets/box-sizing",
  "components/tooltip",
  "utils/form--full",
  "utils/layout",
  "utils/typography",
  "utils/spacing",
  "chameleon/scrollbar"
];

@Component({
  tag: "gx-ide-bpm-sync-gam-roles",
  styleUrl: "bpm-sync-gam-roles.scss",
  shadow: true,
  assetsDirs: ["gx-ide-assets/bpm-sync-gam-roles"]
})
export class GxIdeBpmSyncGamRoles {
  #_componentLocale: any;
  #shortcutsSrc = getAssetPath(
    `./gx-ide-assets/bpm-sync-gam-roles/shortcuts.json`
  );
  #synchronizationTypes: ComboBoxItemModel[] = [];

  @Element() el: HTMLGxIdeBpmSyncGamRolesElement;
  #automaticBackupCheckBoxEl: HTMLChCheckboxElement;
  #chShortcutsEl: HTMLChShortcutsElement;
  #synchTypeComboBoxEl: HTMLChComboBoxRenderElement;

  async componentWillLoad() {
    this.#_componentLocale = await Locale.getComponentStrings(this.el);
    this.#synchronizationTypes = mapTypesToComboBoxItemModel(
      this.#_componentLocale.main.synchronizationTypeOptions
    );
  }

  /**
   * Callback invoked when the user press the 'Cancel' button
   */
  @Prop() readonly cancelCallback: () => Promise<void>;

  /**
   * Callback invoked when the user press the 'Ok' button
   */
  @Prop() readonly saveCallback: (syncData: SyncData) => Promise<boolean>;

  /**
   * Suspends or reactivates the shortcuts
   */
  @Method()
  async suspendShortcuts(suspendShortcuts: boolean) {
    if (suspendShortcuts) {
      this.#chShortcutsEl.suspend = true;
    } else {
      this.#chShortcutsEl.suspend = false;
    }
  }

  #cancelButtonHandler = () => {
    if (this.cancelCallback) {
      this.cancelCallback();
    }
  };
  #okButtonHandler = async () => {
    if (this.saveCallback) {
      const syncData: SyncData = {
        automaticBackup: !!this.#automaticBackupCheckBoxEl?.value,
        syncType: this.#synchTypeComboBoxEl.value as SyncType
      };

      await this.saveCallback(syncData);
    }
  };

  #renderFooter = () => {
    return (
      <footer class="dialog-footer dialog-footer-with-border spacing-body-block-end spacing-body-inline">
        <div class="buttons-spacer">
          <button
            class="button-secondary"
            id="button-cancel"
            onClick={this.#cancelButtonHandler}
            part="button-cancel"
          >
            {this.#_componentLocale.footer.cancelButtonText}
          </button>
          <button
            class="button-primary"
            id="button-create"
            onClick={this.#okButtonHandler}
            part="button-ok"
          >
            {this.#_componentLocale.footer.okButtonText}
          </button>
        </div>
      </footer>
    );
  };

  render() {
    return (
      <Host class="widget">
        <ch-theme model={CSS_BUNDLES}></ch-theme>
        <ch-shortcuts
          src={this.#shortcutsSrc}
          ref={(el: HTMLChShortcutsElement) =>
            (this.#chShortcutsEl = el as HTMLChShortcutsElement)
          }
        ></ch-shortcuts>
        <section class="section spacing-body">
          <div class="main field-group">
            <div class="field field-block">
              <label class="label" htmlFor="synch-type">
                {this.#_componentLocale.main.synchronizationTypeLabel}
              </label>
              <ch-combo-box-render
                accessibleName="Synchronization types"
                class="combo-box"
                id="synch-type"
                value={this.#synchronizationTypes[0].caption}
                model={this.#synchronizationTypes}
                ref={(el: HTMLChComboBoxRenderElement) =>
                  (this.#synchTypeComboBoxEl =
                    el as HTMLChComboBoxRenderElement)
                }
              ></ch-combo-box-render>
            </div>

            <ch-checkbox
              class="checkbox"
              caption={this.#_componentLocale.main.checkBoxLabel}
              checkedValue="true"
              ref={(el: HTMLChCheckboxElement) =>
                (this.#automaticBackupCheckBoxEl = el as HTMLChCheckboxElement)
              }
            ></ch-checkbox>
          </div>
        </section>
        {this.#renderFooter()}
      </Host>
    );
  }
}

export type SyncType = "From GAM" | "To GAM" | "Both";

export type SyncData = {
  syncType: SyncType;
  automaticBackup: boolean;
};
